// Comparison-time normalisation for Slate trees (RES-561 compare feature).
//
// Annotation happens on plain deep clones of the ORIGINAL nodes so render-critical
// props (table columnWidths, styles, …) survive into the compare panes. The volatile
// props below are only ignored when two nodes are compared for equality/signatures —
// they are never diffed and never stripped from what gets rendered.

// Props that drift without the content changing (random ids / float offsets from the
// flowing-page engine, transient editor flags).
const VOLATILE_ELEMENT_PROPS = ['pageBreaks', 'new'];

// Presentation-only props on `table` elements — column resizing and styling must not
// register as a content change.
const VOLATILE_TABLE_PROPS = ['columnWidths', 'rowStyles', 'colStyles', 'styles', 'columnJustify'];

// Engine-generated subtrees: rebuilt from body content by the footnote engine, so
// diffing them would double-report every change. Rendered as-is, never annotated.
const SKIP_DIFF_TYPES = ['footnote-wrapper', 'footnote-content'];

// Block types that carry no comparable content at all.
const IGNORED_TYPES = ['page-break', 'table-drag-handle', 'buffer'];

export function cloneNodes(nodes) {
	return JSON.parse(JSON.stringify(nodes));
}

export function isSkipDiff(node) {
	return !!node && SKIP_DIFF_TYPES.includes(node.type);
}

export function isIgnored(node) {
	return !!node && IGNORED_TYPES.includes(node.type);
}

// Copy of a node with volatile props removed — for comparison only, never rendered.
export function stripVolatile(node) {
	if (Array.isArray(node))
		return node.map(stripVolatile);
	if (!node || typeof node !== 'object')
		return node;
	const copy = {};
	Object.keys(node).forEach((key) => {
		if (VOLATILE_ELEMENT_PROPS.includes(key))
			return;
		if (node.type === 'table' && VOLATILE_TABLE_PROPS.includes(key))
			return;
		copy[key] = node[key];
	});
	if (Array.isArray(copy.children))
		copy.children = copy.children.map(stripVolatile);
	return copy;
}

// JSON.stringify with sorted keys so two logically equal nodes whose keys were
// written in a different order still compare equal.
export function stableStringify(value) {
	if (Array.isArray(value))
		return '[' + value.map(stableStringify).join(',') + ']';
	if (value && typeof value === 'object')
		return '{' + Object.keys(value).sort().map((k) => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
	return JSON.stringify(value) === undefined ? 'null' : JSON.stringify(value);
}

// Deep equality ignoring volatile props.
export function normalisedEqual(a, b) {
	return stableStringify(stripVolatile(a)) === stableStringify(stripVolatile(b));
}
